import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FiActivity, FiAlertTriangle, FiBox, FiClock, FiGlobe, FiLayers, FiRefreshCw, FiSearch } from "react-icons/fi";
import { usePageHeader } from "../../hooks/usePageHeader";
import { listFragments, FragmentInventoryItem, FragmentEnvState } from "./fragmentsClient";

/** Fragments are the shared CDN datasets every dynamic widget/strip reads (markets bars, the header
 *  indicators, "À lire aussi" and "Historique récent"). This page lists all of them with their latest
 *  publishing per environment, so an editor can see at a glance what is live and when it last baked. */

const KIND_META: Record<string, { label: string; hint: string; icon: React.ComponentType<{ className?: string }> }> = {
	block: { label: "Block widgets", hint: "Computed live widgets (markets, header indicators, hub).", icon: FiActivity },
	shared: { label: "Shared strips", hint: "Cross-page card strips (e.g. latest articles).", icon: FiLayers },
	record: { label: "Recent-records strips", hint: "“Historique récent” on each record type's detail pages.", icon: FiBox },
	orphan: { label: "Orphans", hint: "Live on the CDN but no longer declared in code.", icon: FiAlertTriangle },
};
const KIND_ORDER = ["block", "shared", "record", "orphan"];

function formatWhen(iso?: string | null): string {
	if (!iso) return "-";
	const d = new Date(iso);
	if (Number.isNaN(d.getTime())) return "-";
	return d.toLocaleString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

const EnvCell: React.FC<{ state: FragmentEnvState }> = ({ state }) => {
	if (!state.published) {
		return <span className="inline-flex items-center rounded-full border border-border bg-muted px-2 py-0.5 text-xs text-muted-foreground">Not published</span>;
	}
	return (
		<div className="flex flex-col">
			<span className="inline-flex w-fit items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
				<span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Live
			</span>
			<span className="mt-1 inline-flex items-center gap-1 text-[11px] text-muted-foreground">
				<FiClock className="h-3 w-3" /> {formatWhen(state.updatedAt)}
			</span>
		</div>
	);
};

const FragmentsPage: React.FC = () => {
	usePageHeader({ title: "Fragments" });
	const [items, setItems] = useState<FragmentInventoryItem[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [query, setQuery] = useState("");

	const load = async () => {
		setLoading(true);
		setError(null);
		try {
			setItems(await listFragments());
		} catch (e: any) {
			setError(e?.response?.status === 401 ? "Non autorisé." : "Impossible de charger les fragments.");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		load();
	}, []);

	const filtered = useMemo(() => {
		const q = query.trim().toLowerCase();
		if (!q) return items;
		return items.filter((i) => `${i.key} ${i.title} ${i.dependsOn.join(" ")}`.toLowerCase().includes(q));
	}, [items, query]);

	const groups = useMemo(() => {
		const byKind = new Map<string, FragmentInventoryItem[]>();
		for (const item of filtered) {
			const list = byKind.get(item.kind) ?? [];
			list.push(item);
			byKind.set(item.kind, list);
		}
		return KIND_ORDER.filter((k) => byKind.has(k)).map((k) => ({ kind: k, items: byKind.get(k)! }));
	}, [filtered]);

	const stats = useMemo(() => {
		const total = items.length;
		const livePreview = items.filter((i) => i.preview.published).length;
		const liveProd = items.filter((i) => i.production.published).length;
		const orphans = items.filter((i) => i.kind === "orphan").length;
		return { total, livePreview, liveProd, orphans };
	}, [items]);

	return (
		<div className="p-6 space-y-6">
			<div className="flex flex-wrap items-start justify-between gap-3">
				<div>
					<p className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Deployment</p>
					<h1 className="text-xl font-semibold text-foreground">Fragments</h1>
					<p className="max-w-2xl text-sm text-muted-foreground">
						The shared CDN datasets every dynamic widget and strip reads : market bars, the header indicators,
						“À lire aussi” and each type's “Historique récent”. Each shows its latest publishing per environment.
					</p>
				</div>
				<button
					type="button"
					onClick={load}
					className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm font-semibold text-foreground hover:bg-muted"
				>
					<FiRefreshCw className={loading ? "animate-spin" : ""} /> Refresh
				</button>
			</div>

			<div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
				{[
					{ label: "Fragments", value: stats.total },
					{ label: "Live · preview", value: stats.livePreview },
					{ label: "Live · production", value: stats.liveProd },
					{ label: "Orphans", value: stats.orphans },
				].map((s) => (
					<div key={s.label} className="rounded-2xl border border-border bg-card px-4 py-3 shadow-sm">
						<p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{s.label}</p>
						<p className="mt-1 text-2xl font-semibold tabular-nums text-foreground">{s.value}</p>
					</div>
				))}
			</div>

			<div className="relative max-w-md">
				<FiSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
				<input
					value={query}
					onChange={(e) => setQuery(e.target.value)}
					placeholder="Filter by key, title or content type…"
					className="w-full rounded-lg border border-border bg-card py-2 pl-9 pr-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
				/>
			</div>

			{error && (
				<div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>
			)}

			{loading && items.length === 0 ? (
				<div className="rounded-2xl border border-border bg-card px-4 py-10 text-center text-sm text-muted-foreground">Loading…</div>
			) : groups.length === 0 ? (
				<div className="rounded-2xl border border-border bg-card px-4 py-10 text-center text-sm text-muted-foreground">No fragments found.</div>
			) : (
				groups.map(({ kind, items: groupItems }) => {
					const meta = KIND_META[kind] ?? { label: kind, hint: "", icon: FiBox };
					const Icon = meta.icon;
					return (
						<section key={kind} className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
							<div className="flex items-center gap-3 border-b border-border bg-muted/40 px-4 py-3">
								<Icon className="h-4 w-4 text-primary" />
								<div>
									<h2 className="text-sm font-semibold text-foreground">{meta.label} <span className="text-muted-foreground">· {groupItems.length}</span></h2>
									<p className="text-xs text-muted-foreground">{meta.hint}</p>
								</div>
							</div>
							<div className="overflow-x-auto">
								<table className="w-full text-sm">
									<thead className="bg-muted/30 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
										<tr>
											<th className="px-4 py-2.5 text-left">Fragment</th>
											<th className="px-4 py-2.5 text-left">Depends on</th>
											<th className="w-[180px] px-4 py-2.5 text-left">Preview</th>
											<th className="w-[180px] px-4 py-2.5 text-left">Production</th>
											<th className="w-[110px] px-4 py-2.5 text-right">Actions</th>
										</tr>
									</thead>
									<tbody className="divide-y divide-border">
										{groupItems.map((item) => (
											<tr key={`${kind}-${item.key}`} className="align-top hover:bg-muted/30">
												<td className="px-4 py-3">
													<p className="font-medium text-foreground">{item.title}</p>
													<p className="font-mono text-xs text-muted-foreground">{item.key}</p>
												</td>
												<td className="px-4 py-3">
													<div className="flex flex-wrap gap-1">
														{item.dependsOn.length === 0 ? (
															<span className="text-xs text-muted-foreground">-</span>
														) : (
															item.dependsOn.map((d) => (
																<span key={d} className="rounded-md bg-muted px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground">{d}</span>
															))
														)}
													</div>
												</td>
												<td className="px-4 py-3"><EnvCell state={item.preview} /></td>
												<td className="px-4 py-3"><EnvCell state={item.production} /></td>
												<td className="px-4 py-3 text-right">
													{item.preview.published && (
														<Link
															to={`/traduction/fragment?slug=${encodeURIComponent(item.key)}&lang=en`}
															className="inline-flex items-center gap-1 rounded-lg border border-blue-200 bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700 hover:bg-blue-100"
														>
															<FiGlobe className="h-3 w-3" /> Traduire
														</Link>
													)}
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						</section>
					);
				})
			)}
		</div>
	);
};

export default FragmentsPage;
